import type { FloodScrubberRangeHours, FloodScrubberStepHours } from '../types/map.types';

export type BuildFloodScrubberInstantsInput = {
  nowMs: number;
  rangeHours: FloodScrubberRangeHours;
  stepHours: FloodScrubberStepHours;
};

/**
 * @returns Mảng ISO **tăng dần theo thời gian** trong `[now - rangeHours, alignedNow]`, cách bước `stepHours`
 * lùi từ mốc cuối đã làm tròn xuống bước (tránh dịch lưới theo phút).
 * Nếu mốc lùi đầu tiên còn sau `windowStart`, chèn thêm `windowStart` ở đầu mảng.
 */
export function buildFloodScrubberInstants(input: BuildFloodScrubberInstantsInput): string[] {
  const { nowMs, rangeHours, stepHours } = input;
  const rangeMs = rangeHours * 60 * 60 * 1000;
  const stepMs = stepHours * 60 * 60 * 1000;
  if (stepMs <= 0 || !Number.isFinite(nowMs)) {
    return [new Date(nowMs).toISOString()];
  }

  const alignedEnd = Math.floor(nowMs / stepMs) * stepMs;
  const windowStart = nowMs - rangeMs;
  const reversed: string[] = [];
  let t = alignedEnd;
  while (t >= windowStart) {
    reversed.push(new Date(t).toISOString());
    t -= stepMs;
  }
  const asc = reversed.reverse();
  if (asc.length === 0) {
    return [new Date(alignedEnd).toISOString()];
  }
  const firstMs = new Date(asc[0]).getTime();
  if (firstMs > windowStart) {
    asc.unshift(new Date(windowStart).toISOString());
  }
  return asc;
}

/**
 * Tìm index trong `instants` gần `selectedIso` nhất theo mili-giây, hoặc index cuối nếu không parse được.
 */
export function findNearestScrubberIndex(
  instants: readonly string[],
  selectedIso: string | null,
): number {
  if (instants.length === 0) return 0;
  if (selectedIso == null) return instants.length - 1;
  const t = new Date(selectedIso).getTime();
  if (Number.isNaN(t)) return instants.length - 1;
  let best = 0;
  let bestDiff = Infinity;
  for (let i = 0; i < instants.length; i += 1) {
    const ms = new Date(instants[i]).getTime();
    if (Number.isNaN(ms)) continue;
    const d = Math.abs(ms - t);
    if (d < bestDiff) {
      bestDiff = d;
      best = i;
    }
  }
  return best;
}

/**
 * Khi `rangeHours` / `stepHours` đổi, map `selectedIso` cũ sang index hợp lệ tới `instants` mới.
 */
export function clampScrubberIndexToInstants(
  instants: readonly string[],
  selectedIso: string | null,
): { index: number; iso: string } {
  if (instants.length === 0) {
    return { index: 0, iso: new Date().toISOString() };
  }
  const i = findNearestScrubberIndex(instants, selectedIso);
  const safe = Math.min(i, instants.length - 1);
  return { index: safe, iso: instants[safe] };
}

export type FormatScrubberLabelOptions = {
  locale?: string;
};

/**
 * Nhãn thời gian ngắn cho chip trên thanh scrub (ví dụ 24/04, 14:30).
 */
export function formatFloodScrubberLabel(
  iso: string,
  options?: FormatScrubberLabelOptions,
): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(options?.locale ?? 'vi-VN', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Số giờ `iso` lùi trước `nowMs` (âm khi tương lai, 0 khi <30 phút). Làm tròn tới đơn vị giờ.
 */
export function diffHoursBeforeNow(iso: string | null | undefined, nowMs: number): number {
  if (!iso) return 0;
  const t = Date.parse(iso);
  if (Number.isNaN(t) || !Number.isFinite(nowMs)) return 0;
  return Math.round((nowMs - t) / 3600000);
}

/**
 * Giờ hiện tại — wrap để test có thể mock (cùng mô-đun).
 */
export function getFloodScrubberNow(): Date {
  return new Date();
}
